import { defineStore } from 'pinia';
import { computed, type ComputedRef, ref, watchEffect } from 'vue';
import type AccessToken from '@/domain/authentication/AccessToken.ts';
import type Company from '@/domain/model/business/Company.ts';
import type User from '@/domain/model/business/User.ts';
import { identifyUser, resetUser } from '@/infrastructure/analytics/PostHog.ts';
import { useAccessTokenStore } from '@/stores/AccessTokenStore.ts';
import { useCompanyStore } from '@/stores/CompanyStore.ts';
import { useUserStore } from '@/stores/UserStore.ts';

export const useSessionStore = defineStore('session', () => {
  const accessTokenStore = useAccessTokenStore();
  const userStore = useUserStore();
  const companyStore = useCompanyStore();

  const signingOut = ref(false);

  const accessToken: ComputedRef<AccessToken | null> = computed(
    () => accessTokenStore.accessToken,
  );
  const user: ComputedRef<User | null> = computed(() => userStore.user);
  const company: ComputedRef<Company | null> = computed(
    () => companyStore.company,
  );

  const isAdmin: ComputedRef<boolean> = computed(() =>
    Boolean(user.value && user.value.admin),
  );
  const adminMode: ComputedRef<boolean> = computed(() =>
    Boolean(user.value && user.value.admin && userStore.adminMode),
  );

  watchEffect(() => {
    if (user.value && company.value) {
      identifyUser(
        user.value.id,
        company.value.getId(),
        Boolean(user.value.admin),
      );
    }
  });

  function isValid(): boolean {
    return (
      accessToken.value !== null &&
      user.value !== null &&
      company.value !== null
    );
  }

  function isAuthenticated(): boolean {
    return accessToken.value !== null;
  }

  function setAdminMode(value: boolean): void {
    userStore.setAdminMode(value);
  }

  function setSigningOut(value: boolean): void {
    signingOut.value = value;
  }

  function revoke(): void {
    accessTokenStore.clear();
    userStore.clear();
    companyStore.clear();
    resetUser();
  }

  return {
    accessToken,
    user,
    company,
    isAdmin,
    adminMode,
    signingOut,
    isValid,
    isAuthenticated,
    setAdminMode,
    setSigningOut,
    revoke,
  };
});
